/**
 * Persistência local do cache e da fila de ações (Seção 11).
 *
 * O que está aqui é o mínimo para o produto funcionar sem rede: o que já foi
 * lido continua na tela depois de recarregar, e o que foi feito offline sai da
 * fila em ordem quando a conexão volta.
 *
 * ponytail: IndexedDB com o cache do TanStack Query, não SQLite via `wa-sqlite`
 * sobre OPFS como a Seção 7.2 pede. O banco local de verdade só se paga quando
 * a busca local (Seção 9) e o desktop (Seção 19) precisarem do mesmo schema -
 * até lá ele seria um segundo modelo de dados para manter em dia. Ver ADR 0024.
 */

import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

const BANCO = 'waymail';
const LOJA = 'cache';
const CHAVE = 'react-query';

/** Quanto tempo o cache guardado vale. Além disso, a tela começa vazia. */
export const VALIDADE_DO_CACHE = 7 * 24 * 60 * 60 * 1000;

/** Só grava depois que a poeira assenta: uma triagem rápida muda o cache várias vezes por segundo. */
const ESPERA_ENTRE_GRAVACOES = 2000;

let banco: Promise<IDBDatabase> | null = null;

function abrir(): Promise<IDBDatabase> {
  banco ??= new Promise((resolve, reject) => {
    const pedido = indexedDB.open(BANCO, 1);
    pedido.onupgradeneeded = () => pedido.result.createObjectStore(LOJA);
    pedido.onsuccess = () => resolve(pedido.result);
    pedido.onerror = () => reject(pedido.error ?? new Error('IndexedDB indisponivel.'));
  });
  return banco;
}

async function transacionar<T>(
  modo: IDBTransactionMode,
  operacao: (loja: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await abrir();
  return new Promise<T>((resolve, reject) => {
    const pedido = operacao(db.transaction(LOJA, modo).objectStore(LOJA));
    pedido.onsuccess = () => resolve(pedido.result);
    pedido.onerror = () => reject(pedido.error ?? new Error('Falha no IndexedDB.'));
  });
}

/**
 * Guarda o cache no IndexedDB. Sem IndexedDB (jsdom nos testes, navegador em
 * aba anônima com armazenamento bloqueado) tudo vira no-op: o app continua
 * funcionando, só não sobrevive ao recarregamento.
 */
export function criarPersistidor(): Persister {
  const disponivel = typeof indexedDB !== 'undefined';

  let pendente: PersistedClient | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let mutacoesGravadas = -1;

  async function gravar(cliente: PersistedClient): Promise<void> {
    try {
      await transacionar('readwrite', (loja) => loja.put(cliente, CHAVE));
    } catch {
      // Cota estourada ou armazenamento bloqueado. Perder o cache local é
      // aceitável; derrubar a aba por causa dele, não.
    }
  }

  return {
    persistClient(cliente) {
      if (!disponivel) return;
      pendente = cliente;

      /**
       * Com fila pendente, grava na hora; sem fila, espera a janela.
       *
       * Com a janela valendo para tudo, arquivar sem rede e recarregar um
       * segundo depois fazia a acao sumir: nem a mutacao nem o efeito dela na
       * lista tinham chegado ao disco. A pessoa via a mensagem sair da lista e
       * ela voltava. Achado da Fase 9 da campanha de verificacao.
       *
       * Gravar a cada mudanca so vale enquanto ha algo esperando rede, que e
       * justamente quando o disco e a unica copia. Passada a fila, a janela
       * volta a valer e o IndexedDB para de ser escrito a cada clique.
       */
      const mutacoes = cliente.clientState.mutations.length;
      if (mutacoes > 0 || mutacoes !== mutacoesGravadas) {
        mutacoesGravadas = mutacoes;
        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
        pendente = null;
        void gravar(cliente);
        return;
      }

      timer ??= setTimeout(() => {
        timer = null;
        const alvo = pendente;
        pendente = null;
        if (alvo !== null) void gravar(alvo);
      }, ESPERA_ENTRE_GRAVACOES);
    },
    async restoreClient() {
      if (!disponivel) return undefined;
      try {
        return await transacionar<PersistedClient | undefined>('readonly', (loja) =>
          loja.get(CHAVE),
        );
      } catch {
        return undefined;
      }
    },
    async removeClient() {
      if (!disponivel) return;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      pendente = null;
      try {
        await transacionar('readwrite', (loja) => loja.delete(CHAVE));
      } catch {
        // Nada a fazer: já não há o que apagar.
      }
    },
  };
}

/**
 * De quem é o cache guardado nesta máquina.
 *
 * O cache sobrevive ao fim da sessão de propósito - quem volta em cinco minutos
 * reencontra a caixa pintada. Mas se quem entra é outra pessoa, o que está
 * guardado é da anterior e precisa sumir antes de qualquer tela aparecer.
 */
const DONO = 'waymail.dono.v1';

export function donoDoCache(): string | null {
  try {
    return localStorage.getItem(DONO);
  } catch {
    return null;
  }
}

export function definirDonoDoCache(userId: string | null): void {
  try {
    if (userId === null) localStorage.removeItem(DONO);
    else localStorage.setItem(DONO, userId);
  } catch {
    // Sem localStorage o cache simplesmente não é reconhecido na volta.
  }
}
